import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { getChangeLog } from "@/lib/montageplanung";
import type { PlanungChangeLogEntry } from "@/lib/montageplanung";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";

const PAGE_SIZE = 40;

const ACTION_LABELS: Record<string, string> = {
  row_create: "Zeile angelegt",
  row_update: "Zeile geändert",
  row_delete: "Zeile entfernt",
  rows_reorder: "Sortierung",
  assignment_create: "Einsatz angelegt",
  assignment_update: "Einsatz geändert",
  assignment_delete: "Einsatz gelöscht",
  company_event_create: "Betriebsveranstaltung angelegt",
  company_event_update: "Betriebsveranstaltung geändert",
  company_event_delete: "Betriebsveranstaltung gelöscht",
  assignment_links_update: "Verknüpfung geändert",
  assignment_links_remove: "Verknüpfung gelöst",
  assignment_series_update: "Serie geändert",
  assignment_feinplanung_update: "Feinplanung geändert",
  assignment_feinplanung_delegates: "Feinplanung freigegeben",
  unavailability_create: "Nicht verfügbar eingetragen",
  unavailability_update: "Nicht verfügbar geändert",
  unavailability_delete: "Nicht verfügbar entfernt",
  assignment_monteur_proposal_create: "Terminantrag eingereicht",
  assignment_monteur_proposal_update: "Terminantrag geändert",
  assignment_monteur_proposal_office_update: "Terminantrag bearbeitet",
  assignment_monteur_proposal_delete: "Terminantrag zurückgezogen",
  assignment_monteur_proposal_approved: "Terminantrag angenommen",
  assignment_monteur_proposal_rejected: "Terminantrag abgelehnt",
  ablage_schedule: "Aus der Ablage eingeplant",
  ablage_park: "In die Ablage verschoben",
};

function fmtDateTimeDe(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function whoLabel(entry: PlanungChangeLogEntry): string {
  return entry.changed_by_display?.trim() || entry.changed_by_login;
}

export interface HistoryScope {
  title: string;
  entityIds: number[];
}

export interface HistoryDialogProps {
  open: boolean;
  onClose: () => void;
  onViewSnapshot: (logId: number) => void;
  activeLogId?: number | null;
  /** Wenn gesetzt, nur Änderungen an diesem Einsatz (inkl. Verknüpfung). */
  scope?: HistoryScope | null;
  onShowAll?: () => void;
}

export function HistoryDialog({
  open,
  onClose,
  onViewSnapshot,
  activeLogId,
  scope = null,
  onShowAll,
}: HistoryDialogProps) {
  const [items, setItems] = useState<PlanungChangeLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
    }
  }, [open]);

  const entityIds = scope?.entityIds.filter((id) => id > 0).join(",") || "";

  const loadPage = useCallback(
    async (offset: number, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const data = await getChangeLog({
          limit: PAGE_SIZE,
          offset,
          entity_type: entityIds ? "assignment" : undefined,
          entity_ids: entityIds || undefined,
          q: debouncedQuery || undefined,
        });
        setTotal(data.total);
        setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verlauf konnte nicht geladen werden.");
        if (!append) setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedQuery, entityIds],
  );

  useEffect(() => {
    if (!open) return;
    void loadPage(0, false);
  }, [open, loadPage]);

  const hasMore = items.length < total;
  const title = scope ? "Änderungen an diesem Eintrag" : "Änderungsverlauf";
  const description = scope
    ? `Wer „${scope.title}“ angelegt oder verändert hat. Verknüpfte Termine sind mit dabei.`
    : "Wer einen Eintrag im Montageplan angelegt oder verändert hat. Den Stand danach können Sie read-only ansehen.";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="lg"
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Suche nach Eintrag, Person oder Änderung"
            className="min-w-0 flex-1 rounded-lg border border-separator bg-card px-3 py-2 text-sm text-label placeholder:text-label-tertiary"
            aria-label="Änderungsverlauf durchsuchen"
          />
          {scope && onShowAll ? (
            <Button variant="secondary" size="sm" onClick={onShowAll}>
              Gesamter Verlauf
            </Button>
          ) : null}
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        {loading && items.length === 0 ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-label-secondary">
            {scope
              ? "Für diesen Eintrag ist noch nichts protokolliert."
              : "Noch keine Änderungen protokolliert."}
          </p>
        ) : (
          <ul className="max-h-[min(60vh,32rem)] space-y-2 overflow-y-auto pr-1">
            {items.map((entry) => {
              const isActive = activeLogId === entry.id;
              const lines = entry.details ?? [];
              return (
                <li
                  key={entry.id}
                  className={clsx(
                    "rounded-lg border p-3 text-sm",
                    isActive
                      ? "border-accent bg-accent/5"
                      : "border-separator-opaque bg-card-elevated",
                  )}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-card px-2 py-0.5 text-[11px] font-medium text-label-secondary ring-1 ring-separator">
                          {ACTION_LABELS[entry.action] ?? entry.action}
                        </span>
                        <time className="text-xs text-label-tertiary" dateTime={entry.created_at}>
                          {fmtDateTimeDe(entry.created_at)}
                        </time>
                      </div>
                      <p className="font-medium text-label">{entry.summary}</p>
                      {lines.length > 0 ? (
                        <ul className="space-y-0.5 text-xs text-label-secondary">
                          {lines.map((line, index) => (
                            <li key={`${entry.id}-${index}`}>{line}</li>
                          ))}
                        </ul>
                      ) : null}
                      <p className="text-xs text-label-secondary">Von: {whoLabel(entry)}</p>
                    </div>
                    <Button
                      variant={isActive ? "primary" : "secondary"}
                      size="sm"
                      onClick={() => {
                        onViewSnapshot(entry.id);
                        onClose();
                      }}
                    >
                      {isActive ? "Angezeigt" : "Stand ansehen"}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {hasMore && (
          <div className="flex justify-center pt-1">
            <Button
              variant="secondary"
              size="sm"
              disabled={loadingMore}
              onClick={() => void loadPage(items.length, true)}
            >
              {loadingMore ? "Laden…" : `Weitere laden (${items.length}/${total})`}
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
